/**
 * Section B7.5 Page - B.7.5.事前資料參數說明
 * UI: DataTable + 進度列 + Modal（下拉輔助新增）
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import RichTextEditor from '../components/RichTextEditor';
import LatexHelper from '../components/LatexHelper';
import sectionB75Service, {
  SectionB75Detail, ParameterEntry, B74AvailableParam,
} from '../services/sectionB75Service';
import '../styles/DataTable.css';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { useToast } from '../contexts/ToastContext';

// ── 常用單位 ──────────────────────────────────────────────────────
const COMMON_UNITS = [
  { label: 'tCO₂e', latex: '\\text{tCO}_2\\text{e}' },
  { label: 'ktCO₂e', latex: '\\text{ktCO}_2\\text{e}' },
  { label: 'MtCO₂e', latex: '\\text{MtCO}_2\\text{e}' },
  { label: 'MWh', latex: '\\text{MWh}' }, { label: 'GWh', latex: '\\text{GWh}' },
  { label: 'kWh', latex: '\\text{kWh}' }, { label: 'GJ', latex: '\\text{GJ}' },
  { label: 'MJ', latex: '\\text{MJ}' }, { label: 'kg', latex: '\\text{kg}' },
  { label: 't', latex: '\\text{t}' }, { label: 'm³', latex: '\\text{m}^3' },
  { label: 'Nm³', latex: '\\text{Nm}^3' }, { label: 'L', latex: '\\text{L}' },
  { label: '%', latex: '\\%' },
  { label: 'tCO₂e/MWh', latex: '\\text{tCO}_2\\text{e/MWh}' },
  { label: 'tCO₂e/GJ', latex: '\\text{tCO}_2\\text{e/GJ}' },
];

// ── UnitInput ─────────────────────────────────────────────────────
interface UnitInputProps { value: string; onChange: (v: string) => void; disabled?: boolean; }
const UnitInput: React.FC<UnitInputProps> = ({ value, onChange, disabled = false }) => {
  const [showPicker, setShowPicker] = useState(false);
  const preview = () => {
    if (!value.trim()) return null;
    try { return <InlineMath math={value} />; } catch { return <span style={{ color: '#c33', fontSize: 11 }}>!</span>; }
  };
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <input value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
          placeholder="LaTeX，如 \text{tCO}_2\text{e}"
          style={{ flex: 1, padding: '6px 8px', border: '1px solid #d0d0d0', borderRadius: 6, fontFamily: 'monospace', fontSize: 12 }} />
        {value.trim() && (
          <div style={{ minWidth: 50, padding: '2px 6px', background: '#f8faff', border: '1px solid #d0e4ff', borderRadius: 5, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {preview()}
          </div>
        )}
        {!disabled && (
          <button type="button" onClick={() => setShowPicker(p => !p)}
            style={{ padding: '4px 7px', fontSize: 13, borderRadius: 5, border: '1px solid #d0d0d0', background: showPicker ? '#4A90E2' : '#fafafa', color: showPicker ? '#fff' : '#555', cursor: 'pointer' }}>≡</button>
        )}
      </div>
      {showPicker && (
        <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 300, marginTop: 4, background: '#fff', border: '1px solid #d0d0d0', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: '8px 10px', width: 300 }}>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 6, fontWeight: 600 }}>常用單位（點擊插入）</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {COMMON_UNITS.map((u, i) => (
              <button key={i} type="button" onClick={() => { onChange(u.latex); setShowPicker(false); }}
                style={{ padding: '2px 8px', fontSize: 11, borderRadius: 4, border: '1px solid #e0e0e0', background: '#fafafa', cursor: 'pointer' }}>
                {u.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── SaveFeedback ──────────────────────────────────────────────────
function useSaveFeedback() {
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const show = (ok: boolean, text: string) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 2500);
  };
  return { msg, show };
}

// ── InlineMath safe render ────────────────────────────────────────
function SafeInlineMath({ math }: { math: string }) {
  if (!math.trim()) return null;
  try { return <InlineMath math={math} />; } catch { return <span style={{ color: '#c33', fontSize: 11 }}>!</span>; }
}

function SafeBlockMath({ math }: { math: string }) {
  if (!math.trim()) return null;
  try { return <BlockMath math={math} />; } catch { return <span style={{ color: '#c33', fontSize: 11 }}>!</span>; }
}

// ── Purpose tags ──────────────────────────────────────────────────
function PurposeTags({ entry, t }: { entry: ParameterEntry; t: (k: string) => string }) {
  const tags = [];
  if (entry.purpose_baseline) tags.push({ key: 'baseline', label: t('sectionB75.purposeTagBaseline'), color: '#e6f4ff', border: '#91caff', text: '#0958d9' });
  if (entry.purpose_project)  tags.push({ key: 'project',  label: t('sectionB75.purposeTagProject'),  color: '#f6ffed', border: '#b7eb8f', text: '#389e0d' });
  if (entry.purpose_leakage)  tags.push({ key: 'leakage',  label: t('sectionB75.purposeTagLeakage'),  color: '#fff7e6', border: '#ffd591', text: '#d46b08' });
  if (tags.length === 0) return <span style={{ color: '#aaa', fontSize: 12 }}>—</span>;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {tags.map(tag => (
        <span key={tag.key} style={{ padding: '1px 7px', fontSize: 11, borderRadius: 10, background: tag.color, border: `1px solid ${tag.border}`, color: tag.text, fontWeight: 500 }}>
          {tag.label}
        </span>
      ))}
    </div>
  );
}

// ── SourceTag ─────────────────────────────────────────────────────
function SourceTag({ b74Ref, isZh }: { b74Ref: ParameterEntry['b74_ref']; isZh: boolean }) {
  if (b74Ref) {
    return (
      <span style={{ padding: '1px 8px', fontSize: 11, borderRadius: 10, background: '#e6f4ff', border: '1px solid #91caff', color: '#0958d9', fontWeight: 500 }}>
        B.7.4
      </span>
    );
  }
  return (
    <span style={{ padding: '1px 8px', fontSize: 11, borderRadius: 10, background: '#f5f5f5', border: '1px solid #d0d0d0', color: '#888', fontWeight: 500 }}>
      {isZh ? '手動' : 'Manual'}
    </span>
  );
}

// ════════════════════════════════════════════════════════════════
// ParameterModal
// ════════════════════════════════════════════════════════════════
interface ModalProps {
  projectId: number;
  entry: ParameterEntry | null;   // null = 新增模式
  availableB74: B74AvailableParam[];
  canEdit: boolean;
  isZh: boolean;
  onClose: () => void;
  onSaved: (d: SectionB75Detail, continueAdding: boolean) => void;
}

const EMPTY_FORM = {
  dataParameter: '',
  description: '',
  dataUnit: '',
  equationsReferred: '',
  purposeBaseline: false,
  purposeProject: false,
  purposeLeakage: false,
  valuesApplied: '',
  source: null as 'measured' | 'other' | null,
  measurementMethods: '',
  additionalComments: '',
  b74Ref: null as ParameterEntry['b74_ref'],
};

const ParameterModal: React.FC<ModalProps> = ({ projectId, entry, availableB74, canEdit, isZh, onClose, onSaved }) => {
  const { t } = useTranslation();
  const { msg, show } = useSaveFeedback();
  const isNew = entry === null;

  const [form, setForm] = useState(() => isNew ? { ...EMPTY_FORM } : {
    dataParameter: entry?.data_parameter ?? '',
    description: entry?.description ?? '',
    dataUnit: entry?.data_unit ?? '',
    equationsReferred: entry?.equations_referred ?? '',
    purposeBaseline: entry?.purpose_baseline ?? false,
    purposeProject: entry?.purpose_project ?? false,
    purposeLeakage: entry?.purpose_leakage ?? false,
    valuesApplied: entry?.values_applied ?? '',
    source: entry?.source ?? null,
    measurementMethods: entry?.measurement_methods ?? '',
    additionalComments: entry?.additional_comments ?? '',
    b74Ref: entry?.b74_ref ?? null,
  });

  const [selectedB74Symbol, setSelectedB74Symbol] = useState('');
  const [saving, setSaving] = useState(false);
  const [paramRef, setParamRef] = useState<HTMLInputElement | null>(null);

  const insertLatexToParam = (snippet: string) => {
    if (!paramRef) { setForm(f => ({ ...f, dataParameter: f.dataParameter + snippet })); return; }
    const s = paramRef.selectionStart ?? paramRef.value.length;
    const e = paramRef.selectionEnd ?? paramRef.value.length;
    const next = paramRef.value.slice(0, s) + snippet + paramRef.value.slice(e);
    setForm(f => ({ ...f, dataParameter: next }));
    setTimeout(() => { paramRef.focus(); paramRef.setSelectionRange(s + snippet.length, s + snippet.length); }, 10);
  };

  // 選取 B.7.4 參數後自動帶入
  const handleB74Select = (symbol: string) => {
    setSelectedB74Symbol(symbol);
    if (!symbol) {
      setForm(f => ({ ...f, dataParameter: '', dataUnit: '', equationsReferred: '', b74Ref: null }));
      return;
    }
    const param = availableB74.find(p => p.symbol === symbol);
    if (!param) return;
    setForm(f => ({
      ...f,
      dataParameter: param.symbol,
      dataUnit: param.unit || '',
      equationsReferred: param.equation_name ? `B.7.4 ${param.equation_name}` : '',
      b74Ref: {
        symbol: param.symbol,
        para_type: param.para_type,
        equation_name: param.equation_name,
        equation: param.equation,
      },
    }));
  };

  const resetForm = () => {
    setForm({ ...EMPTY_FORM });
    setSelectedB74Symbol('');
  };

  const handleSave = async (continueAdding: boolean) => {
    setSaving(true);
    try {
      let res: SectionB75Detail;
      const payload = {
        data_parameter: form.dataParameter,
        description: form.description,
        data_unit: form.dataUnit,
        equations_referred: form.equationsReferred,
        purpose_baseline: form.purposeBaseline,
        purpose_project: form.purposeProject,
        purpose_leakage: form.purposeLeakage,
        values_applied: form.valuesApplied || null,
        source: form.source,
        measurement_methods: form.measurementMethods || null,
        additional_comments: form.additionalComments || null,
        b74_ref: form.b74Ref,
      };
      if (isNew) {
        res = await sectionB75Service.addParameter(projectId, payload);
      } else {
        res = await sectionB75Service.updateParameter(projectId, entry!.id, payload);
      }
      show(true, t('sectionB75.saveSuccess'));
      onSaved(res, continueAdding);
      if (continueAdding) resetForm();
      else if (!isNew) onClose();
    } catch {
      show(false, t('sectionB75.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const measurementLabel = form.source === 'measured'
    ? t('sectionB75.measurementMethodsLabelMeasured')
    : form.source === 'other'
      ? t('sectionB75.measurementMethodsLabelOther')
      : t('sectionB75.measurementMethodsLabel');

  const titleEntry = form.dataParameter.trim() || form.description.trim()
    ? ` — ${form.dataParameter.trim() || form.description.trim()}`
    : '';

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 1200, maxWidth: '96vw', height: '88vh', background: '#fff', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ padding: '16px 24px', borderBottom: '1px solid #e8e8e8', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>
            {isNew ? t('sectionB75.modalNewTitle') : t('sectionB75.modalEditTitle')}
            {titleEntry && <span style={{ fontWeight: 400, fontSize: 14, color: '#555', marginLeft: 6 }}>{titleEntry}</span>}
          </div>
          <button onClick={onClose} style={{ fontSize: 20, background: 'none', border: 'none', cursor: 'pointer', color: '#888', lineHeight: 1 }}>✕</button>
        </div>

        {/* Form */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>

          {/* B.7.4 下拉選取區（新增模式） */}
          {isNew && (
            <div style={{ background: '#f8faff', border: '1px solid #d0e4ff', borderRadius: 8, padding: '12px 16px', marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#0958d9', marginBottom: 8 }}>
                {isZh
                  ? `從 B.7.4 選取參數（${availableB74.length} 個尚未建立）`
                  : `Select from B.7.4 (${availableB74.length} not yet created)`}
              </div>
              <select value={selectedB74Symbol} onChange={e => handleB74Select(e.target.value)}
                style={{ width: '100%', padding: '7px 10px', border: '1px solid #aac8ff', borderRadius: 6, fontSize: 13, background: '#fff' }}>
                <option value="">{isZh ? '— 不選擇，手動輸入 —' : '— None, enter manually —'}</option>
                {availableB74.map(p => (
                  <option key={p.symbol} value={p.symbol}>
                    {p.symbol}
                    {p.equation_name ? ` — ${p.equation_name}` : ''}
                    {p.para_type === 249 ? (isZh ? '（事前固定）' : ' (Fixed ex ante)') : (isZh ? '（皆適用）' : ' (Applicable to all)')}
                  </option>
                ))}
              </select>
              {availableB74.length === 0 && (
                <div style={{ fontSize: 12, color: '#52c41a', marginTop: 6 }}>
                  ✅ {isZh ? 'B.7.4 所有必要參數已建立' : 'All required B.7.4 parameters created'}
                </div>
              )}
            </div>
          )}

          {/* 來源方程式參考區塊（有 b74_ref 時顯示） */}
          {form.b74Ref && (
            <div style={{ background: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: 8, padding: '12px 16px', marginBottom: 20 }}>
              <div style={{ fontSize: 12, color: '#888', fontWeight: 600, marginBottom: 6 }}>
                {isZh ? '來源方程式（B.7.4）' : 'Source equation (B.7.4)'}
              </div>
              {form.b74Ref.equation_name && (
                <div style={{ fontSize: 13, color: '#444', marginBottom: 4 }}>{form.b74Ref.equation_name}</div>
              )}
              {form.b74Ref.equation && (
                <div style={{ fontSize: 14 }}>
                  <SafeBlockMath math={form.b74Ref.equation} />
                </div>
              )}
            </div>
          )}

          {/* 1. data_parameter */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <label style={{ fontSize: 13, fontWeight: 600 }}>{t('sectionB75.dataParameterLabel')}</label>
              <LatexHelper isZh={isZh} onInsert={insertLatexToParam} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input ref={setParamRef} value={form.dataParameter} onChange={e => setForm(f => ({ ...f, dataParameter: e.target.value }))} disabled={!canEdit}
                placeholder={t('sectionB75.dataParameterPlaceholder')}
                style={{ flex: 1, padding: '7px 10px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 13, fontFamily: 'monospace' }} />
              {form.dataParameter.trim() && (
                <div style={{ minWidth: 60, padding: '4px 10px', background: '#f5f8ff', border: '1px solid #d0e4ff', borderRadius: 6, fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <SafeInlineMath math={form.dataParameter} />
                </div>
              )}
            </div>
          </div>

          {/* 2. description */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>{t('sectionB75.descriptionLabel')}</label>
            <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} disabled={!canEdit}
              placeholder={t('sectionB75.descriptionPlaceholder')}
              style={{ width: '100%', padding: '7px 10px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
          </div>

          {/* 3. data_unit */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>{t('sectionB75.dataUnitLabel')}</label>
            <UnitInput value={form.dataUnit} onChange={v => setForm(f => ({ ...f, dataUnit: v }))} disabled={!canEdit} />
          </div>

          {/* 4. equations_referred */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>{t('sectionB75.equationsReferredLabel')}</label>
            <input value={form.equationsReferred} onChange={e => setForm(f => ({ ...f, equationsReferred: e.target.value }))} disabled={!canEdit}
              placeholder={t('sectionB75.equationsReferredPlaceholder')}
              style={{ width: '100%', padding: '7px 10px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
          </div>

          {/* 5. purpose */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 8 }}>{t('sectionB75.purposeLabel')}</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: canEdit ? 'pointer' : 'default' }}>
                <input type="checkbox" checked={form.purposeBaseline} onChange={e => canEdit && setForm(f => ({ ...f, purposeBaseline: e.target.checked }))} disabled={!canEdit} />
                {t('sectionB75.purposeBaseline')}
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: canEdit ? 'pointer' : 'default' }}>
                <input type="checkbox" checked={form.purposeProject} onChange={e => canEdit && setForm(f => ({ ...f, purposeProject: e.target.checked }))} disabled={!canEdit} />
                {t('sectionB75.purposeProject')}
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: canEdit ? 'pointer' : 'default' }}>
                <input type="checkbox" checked={form.purposeLeakage} onChange={e => canEdit && setForm(f => ({ ...f, purposeLeakage: e.target.checked }))} disabled={!canEdit} />
                {t('sectionB75.purposeLeakage')}
              </label>
            </div>
          </div>

          {/* 6. values_applied */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>{t('sectionB75.valuesAppliedLabel')}</label>
            <RichTextEditor value={form.valuesApplied} onChange={v => setForm(f => ({ ...f, valuesApplied: v }))} disabled={!canEdit} minHeight="96px" />
          </div>

          {/* 7. source */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 8 }}>{t('sectionB75.sourceLabel')}</label>
            <div style={{ display: 'flex', gap: 24 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: canEdit ? 'pointer' : 'default' }}>
                <input type="radio" name="b75-source" value="measured" checked={form.source === 'measured'} onChange={() => canEdit && setForm(f => ({ ...f, source: 'measured' }))} disabled={!canEdit} />
                {t('sectionB75.sourceMeasured')}
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: canEdit ? 'pointer' : 'default' }}>
                <input type="radio" name="b75-source" value="other" checked={form.source === 'other'} onChange={() => canEdit && setForm(f => ({ ...f, source: 'other' }))} disabled={!canEdit} />
                {t('sectionB75.sourceOther')}
              </label>
            </div>
          </div>

          {/* 8. measurement_methods */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>{measurementLabel}</label>
            <RichTextEditor value={form.measurementMethods} onChange={v => setForm(f => ({ ...f, measurementMethods: v }))} disabled={!canEdit} minHeight="96px" />
          </div>

          {/* 9. additional_comments */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
              {t('sectionB75.additionalCommentsLabel')} <span style={{ color: '#aaa', fontWeight: 400 }}>({t('sectionB75.optional')})</span>
            </label>
            <RichTextEditor value={form.additionalComments} onChange={v => setForm(f => ({ ...f, additionalComments: v }))} disabled={!canEdit} minHeight="72px" />
          </div>

          {/* Buttons */}
          {canEdit && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              {isNew && (
                <button onClick={() => handleSave(true)} disabled={saving}
                  style={{ padding: '8px 20px', fontSize: 14, borderRadius: 6, border: 'none', background: saving ? '#ccc' : '#52c41a', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
                  {saving ? t('sectionB75.saving') : (isZh ? '儲存並繼續新增' : 'Save & Add Next')}
                </button>
              )}
              <button onClick={() => handleSave(false)} disabled={saving}
                style={{ padding: '8px 20px', fontSize: 14, borderRadius: 6, border: 'none', background: saving ? '#ccc' : '#4A90E2', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
                {saving ? t('sectionB75.saving') : (isNew ? (isZh ? '儲存並關閉' : 'Save & Close') : t('sectionB75.save'))}
              </button>
              {msg && <span style={{ fontSize: 12, color: msg.ok ? '#52c41a' : '#e53935' }}>{msg.text}</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// ════════════════════════════════════════════════════════════════
// Main Page
// ════════════════════════════════════════════════════════════════
const SectionB75Page: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh-TW';
  const { selectedProjectId } = useProject();
  const { hasPermission } = usePermission();
  const toast = useToast();
  const canUpdate = hasPermission('section_b7_5', 'update');

  const [detail, setDetail] = useState<SectionB75Detail | null>(null);
  const [availableB74, setAvailableB74] = useState<B74AvailableParam[]>([]);
  const [b74Total, setB74Total] = useState(0);
  const [b74Built, setB74Built] = useState(0);
  const [loading, setLoading] = useState(false);
  const [modalEntry, setModalEntry] = useState<ParameterEntry | null | undefined>(undefined); // undefined = closed
  const [resetConfirm, setResetConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const entries: ParameterEntry[] = detail?.parameters ?? [];

  // ── Load ──────────────────────────────────────────────────────
  const loadData = useCallback(async (projectId: number) => {
    setLoading(true);
    try {
      await validateSession('SectionB75Page');
      const [res, b74Res] = await Promise.all([
        sectionB75Service.getSectionB75ByProject(projectId),
        sectionB75Service.getB74AvailableParams(projectId),
      ]);
      setDetail(res);
      setAvailableB74(b74Res.available);
      setB74Total(b74Res.total);
      setB74Built(b74Res.built);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (selectedProjectId) { setDetail(null); setModalEntry(undefined); loadData(selectedProjectId); }
  }, [selectedProjectId, loadData]);

  // ── onSaved: update state + reload available params ───────────
  const handleSaved = (d: SectionB75Detail, continueAdding: boolean) => {
    setDetail(d);
    setB74Built(d.built);
    setB74Total(d.total);
    // reload available list
    if (selectedProjectId) {
      sectionB75Service.getB74AvailableParams(selectedProjectId).then(r => {
        setAvailableB74(r.available);
        setB74Total(r.total);
        setB74Built(r.built);
      });
    }
    if (!continueAdding) setModalEntry(undefined);
  };

  // ── Delete ────────────────────────────────────────────────────
  const handleDelete = async (entryId: string) => {
    if (!selectedProjectId) return;
    try {
      await sectionB75Service.deleteParameter(selectedProjectId, entryId);
      loadData(selectedProjectId);
    } catch { toast.error(t('sectionB75.deleteFailed')); }
    setDeleteTarget(null);
  };

  // ── Reset ─────────────────────────────────────────────────────
  const handleReset = async () => {
    if (!selectedProjectId) return;
    try {
      await sectionB75Service.resetSectionB75(selectedProjectId);
      setDetail(null);
      setB74Built(0);
      if (selectedProjectId) {
        sectionB75Service.getB74AvailableParams(selectedProjectId).then(r => {
          setAvailableB74(r.available);
          setB74Total(r.total);
          setB74Built(r.built);
        });
      }
    } catch { /* ignore */ }
    setResetConfirm(false);
  };

  // ── Progress bar ──────────────────────────────────────────────
  const allDone = b74Total > 0 && b74Built >= b74Total;
  const progressPct = b74Total > 0 ? Math.round(b74Built / b74Total * 100) : (entries.length > 0 ? 100 : 0);

  const showReset = canUpdate && entries.length > 0;

  return (
    <div className="page-container">
      {/* Page Header */}
      <div className="page-header">
        <FunctionPageHeader funcCode="section_b7_5" />
        {showReset && (
          <div className="page-header-right">
            <button onClick={() => setResetConfirm(true)}
              style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #e53935', background: '#fff', color: '#e53935', cursor: 'pointer', fontWeight: 500, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}
              title={t('sectionB75.resetTitle')}>
              🗑 {t('sectionB75.resetData')}
            </button>
          </div>
        )}
      </div>

      {/* 未選專案 / 內容區 */}
      {!selectedProjectId ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: 16 }}>
          {t('sectionB75.selectProject')}
        </div>
      ) : loading ? (
        <div className="loading">{t('sectionB75.loading')}</div>
      ) : (
        <div>
          {/* 進度列 + 新增按鈕 */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, padding: '10px 16px', background: allDone ? '#f6ffed' : '#fffbe6', border: `1px solid ${allDone ? '#b7eb8f' : '#ffe58f'}`, borderRadius: 8 }}>
            <div style={{ fontSize: 13, color: allDone ? '#389e0d' : '#d46b08', fontWeight: 500 }}>
              {b74Total === 0 ? (
                isZh ? 'B.7.4 尚無必要參數資料，可手動新增' : 'No required parameters in B.7.4, add manually'
              ) : allDone ? (
                `✅ ${isZh ? 'B.7.4 所有必要參數已建立' : 'All required B.7.4 parameters created'} (${b74Built} / ${b74Total})`
              ) : (
                `${isZh ? 'B.7.4 必要參數：已建立' : 'B.7.4 required: created'} ${b74Built} / ${b74Total}　${isZh ? '尚餘' : 'remaining'} ${b74Total - b74Built} ${isZh ? '個未建立' : 'not yet created'}`
              )}
            </div>
            {canUpdate && (
              <button onClick={() => setModalEntry(null)}
                style={{ padding: '7px 18px', fontSize: 13, borderRadius: 6, border: 'none', background: '#4A90E2', color: '#fff', cursor: 'pointer', fontWeight: 600, flexShrink: 0 }}>
                + {t('sectionB75.addParameter')}
              </button>
            )}
          </div>

          {/* DataTable */}
          {entries.length === 0 ? (
            <div style={{ color: '#aaa', fontSize: 14, padding: '24px 0' }}>{t('sectionB75.noData')}</div>
          ) : (
            <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead className="table-header-dark-green">
                <tr>
                  <th style={{ width: 44, textAlign: 'center' }}>#</th>
                  <th style={{ width: 80 }}>{isZh ? '來源' : 'Source'}</th>
                  <th>{t('sectionB75.colParameter')}</th>
                  <th>{t('sectionB75.colDescription')}</th>
                  <th>{t('sectionB75.colUnit')}</th>
                  <th>{t('sectionB75.colPurpose')}</th>
                  <th>{t('sectionB75.colSource')}</th>
                  <th style={{ width: 160, textAlign: 'center' }}>{t('sectionB75.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry, idx) => (
                  <tr key={entry.id}>
                    <td style={{ textAlign: 'center', color: '#888' }}>{idx + 1}</td>
                    <td style={{ textAlign: 'center' }}>
                      <SourceTag b74Ref={entry.b74_ref} isZh={isZh} />
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {entry.data_parameter ? <SafeInlineMath math={entry.data_parameter} /> : <span style={{ color: '#aaa' }}>—</span>}
                    </td>
                    <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={entry.description}>
                      {entry.description || <span style={{ color: '#aaa' }}>—</span>}
                    </td>
                    <td>
                      {entry.data_unit ? <SafeInlineMath math={entry.data_unit} /> : <span style={{ color: '#aaa' }}>—</span>}
                    </td>
                    <td><PurposeTags entry={entry} t={t} /></td>
                    <td>
                      {entry.source === 'measured' ? (
                        <span style={{ padding: '2px 8px', fontSize: 11, borderRadius: 10, background: '#e6f4ff', border: '1px solid #91caff', color: '#0958d9' }}>{t('sectionB75.sourceMeasured')}</span>
                      ) : entry.source === 'other' ? (
                        <span style={{ padding: '2px 8px', fontSize: 11, borderRadius: 10, background: '#f9f0ff', border: '1px solid #d3adf7', color: '#531dab' }}>{t('sectionB75.sourceOther')}</span>
                      ) : (
                        <span style={{ color: '#aaa' }}>—</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button onClick={() => setModalEntry(entry)}
                        style={{ padding: '3px 12px', fontSize: 12, borderRadius: 5, border: '1px solid #4A90E2', background: '#fff', color: '#4A90E2', cursor: 'pointer', marginRight: 6 }}>
                        {t('sectionB75.edit')}
                      </button>
                      {canUpdate && (
                        <button onClick={() => setDeleteTarget(entry.id)}
                          style={{ padding: '3px 12px', fontSize: 12, borderRadius: 5, border: '1px solid #ff4d4f', background: '#fff', color: '#ff4d4f', cursor: 'pointer' }}>
                          {t('sectionB75.delete')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Parameter Modal */}
      {modalEntry !== undefined && selectedProjectId && (
        <ParameterModal
          projectId={selectedProjectId}
          entry={modalEntry}
          availableB74={availableB74}
          canEdit={canUpdate}
          isZh={isZh}
          onClose={() => setModalEntry(undefined)}
          onSaved={handleSaved}
        />
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: '28px 32px', minWidth: 320, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
            <div style={{ fontSize: 15, marginBottom: 20 }}>{t('sectionB75.deleteConfirm')}</div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setDeleteTarget(null)}
                style={{ padding: '6px 18px', borderRadius: 6, border: '1px solid #d0d0d0', background: '#fff', cursor: 'pointer' }}>
                {t('sectionB75.cancel')}
              </button>
              <button onClick={() => handleDelete(deleteTarget)}
                style={{ padding: '6px 18px', borderRadius: 6, border: 'none', background: '#ff4d4f', color: '#fff', cursor: 'pointer' }}>
                {t('sectionB75.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset confirm */}
      {resetConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: '28px 32px', minWidth: 360, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>{t('sectionB75.resetTitle')}</div>
            <div style={{ fontSize: 13, color: '#555', marginBottom: 20, whiteSpace: 'pre-line' }}>{t('sectionB75.resetConfirm')}</div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setResetConfirm(false)}
                style={{ padding: '6px 18px', borderRadius: 6, border: '1px solid #d0d0d0', background: '#fff', cursor: 'pointer' }}>
                {t('sectionB75.cancel')}
              </button>
              <button onClick={handleReset}
                style={{ padding: '6px 18px', borderRadius: 6, border: 'none', background: '#ff4d4f', color: '#fff', cursor: 'pointer' }}>
                {t('sectionB75.reset')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SectionB75Page;
